import { SectionHeader } from '../components/shared/SectionHeader';
import { CTAStrip } from '../components/shared/CTAStrip';
import {
  CheckCircle2,
  FileCheck,
  Globe,
  Shield,
  Eye,
  Settings,
  QrCode,
  FileText,
  Scale
} from 'lucide-react';

const standardsFeatures = [
  {
    icon: FileCheck,
    title: "Structured Certification Processes",
    description: "Formalized workflows that ensure every certificate follows a consistent, auditable path from application to issuance."
  },
  {
    icon: Eye,
    title: "Complete Traceability",
    description: "Every action is logged and timestamped, providing a complete audit trail for regulatory compliance and accountability."
  },
  {
    icon: FileText,
    title: "Consistent Documentation",
    description: "Standardized certificate templates with fixed layouts ensure uniform presentation across all issued documents."
  },
  {
    icon: Scale,
    title: "Audit Readiness",
    description: "Built-in reporting and record-keeping designed to meet audit requirements and regulatory inspections."
  },
  {
    icon: Globe,
    title: "Cross-border Trust",
    description: "Security features and verification capabilities that trading partners recognize and trust."
  },
  {
    icon: Settings,
    title: "National Configurability",
    description: "Adaptable to national regulations and specific export market requirements without custom development."
  }
];

const integrityFeatures = [
  {
    icon: FileText,
    title: "Fixed-layout Templates",
    description: "Official certificate format maintained consistently across all issuances, preventing unauthorized modifications."
  },
  {
    icon: Shield,
    title: "Watermarks & Security Text",
    description: "Diagonal security text and official watermarks embedded in every certificate PDF."
  },
  {
    icon: QrCode,
    title: "Barcode & QR Verification",
    description: "Unique identifiers that can be scanned to verify certificate authenticity against the central database."
  },
  {
    icon: Eye,
    title: "Controlled Issuance",
    description: "Certificates only generated after proper approval workflows, with full tracking of who issued what and when."
  }
];

export default function StandardsPage() {
  return (
    <div data-testid="standards-page">
      {/* Hero */}
      <section className="section-padding bg-slate-50 leaf-pattern">
        <div className="container-custom">
          <div className="max-w-3xl mx-auto text-center">
            <span className="badge-green mb-4 inline-block">Standards & Compliance</span>
            <h1 className="text-4xl lg:text-5xl font-bold text-slate-900 mb-6">
              Built for 
              <span className="text-[#1F7A3A]"> international compliance</span>
            </h1>
            <p className="text-lg text-slate-600">
              Certisfy aligns with international phytosanitary norms and best practices, 
              providing the structure and traceability required for trusted cross-border agricultural trade.
            </p>
          </div>
        </div>
      </section>

      {/* Standards Alignment */}
      <section data-testid="standards-alignment" className="section-padding bg-white">
        <div className="container-custom">
          <SectionHeader 
            badge="Compliance Framework"
            title="Aligned with international norms"
            subtitle="Our platform is designed to support compliance with international phytosanitary standards and national regulatory requirements"
          />

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 mt-12">
            {standardsFeatures.map((feature, index) => (
              <div key={index} className="card-feature group" data-testid={`standard-${index}`}>
                <div className="icon-container feature-icon-hover mb-4">
                  <feature.icon className="w-6 h-6" />
                </div>
                <h3 className="text-xl font-bold text-slate-900 mb-3">{feature.title}</h3>
                <p className="text-slate-600">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Configurability Note */}
      <section className="py-16 bg-[#1F7A3A]">
        <div className="container-custom">
          <div className="max-w-3xl mx-auto text-center text-white">
            <h2 className="text-2xl lg:text-3xl font-bold mb-4">
              Configurable to your national requirements
            </h2>
            <p className="text-green-100 text-lg">
              Every country has specific regulations and export market requirements. Certisfy's 
              rules engine and configurable workflows adapt to your national framework without 
              requiring custom development or code changes.
            </p>
          </div>
        </div>
      </section>

      {/* Certificate Integrity */}
      <section data-testid="certificate-integrity" className="section-padding bg-white">
        <div className="container-custom">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <span className="badge-gold mb-4 inline-block">Document Security</span>
              <h2 className="text-3xl lg:text-4xl font-bold text-slate-900 mb-6">
                Certificate integrity you can trust
              </h2>
              <p className="text-lg text-slate-600 mb-8">
                Every certificate generated by Certisfy includes multiple security features 
                designed to prevent fraud and enable easy verification by trade partners worldwide.
              </p>

              <div className="space-y-6">
                {integrityFeatures.map((feature, index) => (
                  <div key={index} className="flex items-start gap-4">
                    <div className="w-12 h-12 rounded-xl bg-[#FFFBEB] flex items-center justify-center flex-shrink-0">
                      <feature.icon className="w-6 h-6 text-[#C8A24A]" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-slate-900 mb-1">{feature.title}</h3>
                      <p className="text-slate-600 text-sm">{feature.description}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Certificate Preview Mock */}
            <div className="relative">
              <div className="bg-white rounded-2xl shadow-float p-8 border border-slate-200">
                <div className="border-2 border-dashed border-slate-300 rounded-xl p-6 relative">
                  {/* Mock certificate content */}
                  <div className="absolute top-0 left-0 right-0 h-2 gold-accent rounded-t-lg" />
                  <div className="text-center mb-6 pt-4">
                    <p className="text-xs text-slate-400 uppercase tracking-wider mb-2">Official Document</p>
                    <h3 className="text-xl font-bold text-[#1F7A3A]">Phytosanitary Certificate</h3>
                  </div>
                  
                  <div className="space-y-3 text-sm text-slate-600">
                    <div className="flex justify-between">
                      <span className="text-slate-400">Certificate No:</span>
                      <span className="font-mono">PHY-2024-00001234</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-slate-400">Exporter:</span>
                      <span>Fresh Produce SA Ltd</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-slate-400">Destination:</span>
                      <span>United Kingdom</span>
                    </div>
                  </div>

                  {/* Watermark simulation */}
                  <div className="absolute inset-0 flex items-center justify-center opacity-5 pointer-events-none">
                    <p className="text-6xl font-bold text-[#1F7A3A] rotate-[-30deg]">CERTIFIED</p>
                  </div>

                  {/* QR Code mock */}
                  <div className="mt-6 pt-4 border-t border-slate-200 flex items-center justify-between">
                    <div className="w-16 h-16 bg-slate-100 rounded-lg flex items-center justify-center">
                      <QrCode className="w-10 h-10 text-slate-400" />
                    </div>
                    <div className="text-right text-xs text-slate-400">
                      <p>Scan to verify</p>
                      <p>authenticity</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Decorative */}
              <div className="absolute -bottom-6 -right-6 w-32 h-32 bg-[#C8A24A]/10 rounded-full -z-10" />
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <CTAStrip 
        title="See compliance in action"
        subtitle="Learn how Certisfy can help your organization meet regulatory requirements."
      />
    </div>
  );
}
